import { useMemo } from 'react'

import { buildDayColumn, DayColumnData } from './buildDayColumn'
import { MY_TEAM_CONSTANTS } from '../../const'
import { MyTeamEmployeeData } from '../../types'
import { formatDateKey } from '../../utils/dateUtils'
import { roundDays, roundHours } from '../../utils/format'

export type { DayColumnData }

export const calculateSummaryMetrics = (employees: MyTeamEmployeeData[]) => {
  const totalHours = roundHours(
    employees.reduce((sum, e) => sum + e.totalHours, 0),
  )
  const totalBillableHours = employees.reduce(
    (sum, e) => sum + e.billableHours,
    0,
  )
  const totalNonBillableHours = employees.reduce(
    (sum, e) => sum + e.nonBillableHours,
    0,
  )
  const avgUtilization =
    employees.length > 0
      ? Math.round(
          employees.reduce((sum, e) => sum + e.utilization, 0) /
            employees.length,
        )
      : 0

  return {
    totalTrackedDays: roundDays(totalHours, MY_TEAM_CONSTANTS.HOURS_PER_DAY),
    totalTrackedHours: roundHours(totalHours),
    totalBillableDays: roundDays(
      totalBillableHours,
      MY_TEAM_CONSTANTS.HOURS_PER_DAY,
    ),
    totalBillableHours: roundHours(totalBillableHours),
    totalNonBillableDays: roundDays(
      totalNonBillableHours,
      MY_TEAM_CONSTANTS.HOURS_PER_DAY,
    ),
    totalNonBillableHours: roundHours(totalNonBillableHours),
    avgUtilization,
  }
}

export const useMyTeamSummary = (
  employees: MyTeamEmployeeData[],
  daysInMonth: Date[],
) => {
  const summaryMetrics = useMemo(
    () => calculateSummaryMetrics(employees),
    [employees],
  )

  const dayData = useMemo(
    () =>
      daysInMonth.reduce((map, day) => {
        const dateKey = formatDateKey(day)
        map.set(dateKey, buildDayColumn(employees, dateKey))
        return map
      }, new Map<string, DayColumnData>()),
    [employees, daysInMonth],
  )

  const allRecordIds = useMemo(
    () =>
      employees.flatMap((e) => Array.from(e.dailyRecordIds.values()).flat()),
    [employees],
  )

  return { summaryMetrics, dayData, allRecordIds }
}
